import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { withSuppressSuccessToast } from '@core/http/api-response.envelope';
import {
  VerificationRequiredResponse,
  VerifyCodeRequest,
  VerifyCodeResponse,
} from '../models/verification.model';

/**
 * Shared service for the verification-management OTP / TOTP step that
 * gates feature mutations (broadcast, approve, reject, …).
 *
 * The success-toast interceptor is suppressed because the verify call is
 * never the response whose envelope message we want to surface — the
 * subsequent feature confirmation call owns that toast.
 */
@Injectable({
  providedIn: 'root',
})
export class VerificationService {
  private readonly http = inject(HttpClient);
  private readonly verificationManagementApiUrl = `${environment.verificationManagementApiUrl}/api/v1`;

  /**
   * POST `/api/v1/verification/verify` — submits the user-entered OTP /
   * TOTP code for the active verification handle.
   *
   * On `verified=true` the caller must retry the original mutation with
   * the same `actionId` to actually record the action.
   */
  verifyCode(actionId: string, code: string): Observable<VerifyCodeResponse> {
    const url = `${this.verificationManagementApiUrl}/verification/verify`;
    const body: VerifyCodeRequest = { actionId, code };
    return this.http.post<VerifyCodeResponse>(url, body, {
      context: withSuppressSuccessToast(),
    });
  }

  /**
   * Coerce a 202 `verification_required` body into a plain-JSON shape.
   *
   * Some backends serialize `expiresAt` as a Long-shaped object
   * (`{ low, high, unsigned }`); we normalize it to a Unix-millisecond
   * number here so consumers don't have to special-case that.
   */
  normalizeVerificationRequired<T extends VerificationRequiredResponse>(
    body: T
  ): T {
    const raw = body?.expiresAt as unknown;
    const expiresAt =
      typeof raw === 'number'
        ? raw
        : raw && typeof raw === 'object' && 'low' in (raw as object)
          ? this.longLikeToNumber(
              raw as { low: number; high: number; unsigned?: boolean }
            )
          : 0;
    return { ...body, expiresAt };
  }

  private longLikeToNumber(value: {
    low: number;
    high: number;
    unsigned?: boolean;
  }): number {
    // Mirrors protobuf-style Long → number conversion. Safe for
    // timestamps, which fit comfortably inside Number.MAX_SAFE_INTEGER.
    return value.high * 0x1_0000_0000 + (value.low >>> 0);
  }
}
